import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useAuthStore } from '@/stores/auth';
import { progressApi } from '@/api/progress';
import { api } from '@/api/client';
import { humanizeError } from '@/lib/errors';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card, CardTitle } from '@/components/ui/Card';
import { Pill } from '@/components/ui/Pill';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useShellContext } from '@/components/layout/shellContext';
import { splitHomeworkContent } from '@/lib/homeworkContent';
import { MBlock, MEmpty, MIcon, MRow, MSection } from '@/components/mobile/MobileKit';

export function CuratorDashboardPage() {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const isMobile = useIsMobile();
  // Цифра колокольчика — та же, что на «Ещё» в нижнем меню: считает оболочка, второго опроса нет.
  const unread = useShellContext()?.unread ?? 0;
  const pending = useQuery({ queryKey: ['curator', 'pending'], queryFn: () => progressApi.listPendingHomework('pending') });
  // Use the curator-scoped endpoint (the /admin/users endpoint is admin-only and
  // returns 403 for curators). The response is a bare array of student rows.
  const students = useQuery({
    queryKey: ['curator', 'students-count'],
    queryFn: async () => {
      const { data } = await api.get('/curator/students');
      const arr = (Array.isArray(data) ? data : ((data as { items?: unknown[] })?.items ?? [])) as unknown[];
      return arr.length;
    },
  });

  // ── Телефон (docs/CURATOR-MOBILE-DESIGN.md §4.5) ──────────────────────────
  if (isMobile) {
    const name = user?.full_name?.split(' ')[0] ?? t('curatorDashboard.defaultName');
    const tiles = [
      { to: '/curator/students', value: students.data ?? '—', label: t('curatorDashboard.activeStudents') },
      { to: '/curator/homework', value: pending.data?.total ?? '—', label: t('curatorDashboard.hwPending') },
      {
        to: '/curator/chat',
        value: <span className="material-symbols-outlined text-[22px]">chat</span>,
        label: t('curatorDashboard.chats'),
      },
    ];
    return (
      <div className="space-y-3">
        <div className="flex items-start gap-2.5 pb-1 pt-1.5">
          <div className="min-w-0 flex-1">
            <h1 className="font-display text-[22px] font-bold leading-tight text-on-surface">
              {t('curatorDashboard.welcome', { name })}
            </h1>
            <p className="mt-0.5 text-[11.5px] text-on-surface-variant">{t('curatorDashboard.subtitle')}</p>
          </div>
          {/* Колокольчик — как у ученика на главной: уведомления в одно касание, не через «Ещё». */}
          <Link
            to="/curator/notifications"
            aria-label={t('nav.notifications')}
            className="relative grid h-11 w-11 flex-none place-items-center rounded-full bg-surface-container-low text-on-surface"
          >
            <span className="material-symbols-outlined text-[20px]">notifications</span>
            {unread > 0 && (
              <span className="absolute -right-0.5 -top-0.5 min-w-[18px] rounded-full bg-primary px-1 text-center text-[10px] font-bold leading-[18px] text-on-primary">
                {unread > 99 ? '99+' : unread}
              </span>
            )}
          </Link>
        </div>

        {/* Три числа — каждое ведёт в свой раздел. */}
        <div className="grid grid-cols-3 gap-2">
          {tiles.map((tile) => (
            <Link key={tile.to} to={tile.to} className="rounded-[22px] bg-surface-container-low px-3 py-3">
              <div className="font-display text-[19px] font-bold text-on-surface">{tile.value}</div>
              <div className="text-[10.5px] leading-tight text-on-surface-variant">{tile.label}</div>
            </Link>
          ))}
        </div>

        <MSection
          title={t('curatorDashboard.hwAwaitingReview')}
          right={<Link to="/curator/homework" className="text-primary">{t('curatorDashboard.all')}</Link>}
        />
        {pending.isLoading ? (
          <p className="px-0.5 text-[13px] text-on-surface-variant">{t('common.loading')}</p>
        ) : pending.isError ? (
          <p className="px-0.5 text-[13px] text-error">{humanizeError(pending.error)}</p>
        ) : pending.data?.items.length ? (
          <MBlock>
            {pending.data.items.slice(0, 5).map((hw, i, arr) => (
              <MRow
                key={hw.id}
                // Сразу окно проверки этой работы (§4.5), а не общий список: очередь открывает его по ?submission=.
                to={`/curator/homework?submission=${hw.id}`}
                leading={<MIcon icon={splitHomeworkContent(hw.content).audio.length ? 'mic' : 'description'} tone="warm" />}
                title={hw.student_name || t('curatorDashboard.homework')}
                meta={hw.lesson_title || hw.section_title || t('curatorDashboard.homework')}
                // Время сдачи — справа, как в очереди домашки: в подписи после длинного урока оно обрезалось до
                // «Сдано …» (кадр 390 стенда, 13.10.2026).
                trailing={
                  <span className="flex-none text-[10.5px] tabular-nums text-on-surface-variant">
                    {hw.submitted_at
                      ? new Date(hw.submitted_at).toLocaleString('ru-RU', {
                          day: '2-digit',
                          month: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit',
                        })
                      : t('curatorDashboard.submittedAtUnknown')}
                  </span>
                }
                last={i === arr.length - 1}
              />
            ))}
          </MBlock>
        ) : (
          <MEmpty icon="task_alt" title={t('curatorDashboard.allReviewed')} />
        )}
      </div>
    );
  }

  return (
    <>
      <PageHeader title={t('curatorDashboard.welcome', { name: user?.full_name?.split(' ')[0] ?? t('curatorDashboard.defaultName') })} subtitle={t('curatorDashboard.subtitle')} />

      <section className="grid gap-3 md:grid-cols-3">
        <Link to="/curator/students">
          <Card interactive className="flex items-center gap-4">
            <span className="material-symbols-outlined rounded-button bg-primary-fixed p-2 text-2xl text-on-primary-fixed-variant">groups</span>
            <div>
              <p className="text-xs uppercase tracking-label text-on-surface-variant">{t('curatorDashboard.activeStudents')}</p>
              <p className="font-display text-3xl font-extrabold tracking-display">{students.data ?? '—'}</p>
            </div>
          </Card>
        </Link>
        <Link to="/curator/homework">
          <Card interactive className="flex items-center gap-4">
            <span className="material-symbols-outlined rounded-button bg-secondary-fixed p-2 text-2xl text-on-secondary-container">assignment</span>
            <div>
              <p className="text-xs uppercase tracking-label text-on-surface-variant">{t('curatorDashboard.hwPending')}</p>
              <p className="font-display text-3xl font-extrabold tracking-display">{pending.data?.total ?? '—'}</p>
            </div>
          </Card>
        </Link>
        <Link to="/curator/chat">
          <Card interactive className="flex items-center gap-4">
            <span className="material-symbols-outlined rounded-button bg-tertiary-fixed p-2 text-2xl text-on-tertiary-fixed-variant">chat</span>
            <div>
              <p className="text-xs uppercase tracking-label text-on-surface-variant">{t('curatorDashboard.chats')}</p>
              <p className="font-display text-xl font-bold tracking-display">{t('curatorDashboard.openChat')}</p>
            </div>
          </Card>
        </Link>
      </section>

      <section className="mt-8">
        <div className="mb-3 flex items-end justify-between">
          <h2 className="font-display text-2xl font-bold tracking-display">{t('curatorDashboard.hwAwaitingReview')}</h2>
          <Link to="/curator/homework" className="text-sm font-semibold text-primary">{t('curatorDashboard.all')}</Link>
        </div>
        {pending.isLoading ? (
          <Card variant="subtle">{t('common.loading')}</Card>
        ) : pending.isError ? (
          <Card variant="subtle" className="text-center text-error">{humanizeError(pending.error)}</Card>
        ) : pending.data?.items.length ? (
          <ul className="space-y-2">
            {pending.data.items.slice(0, 5).map((hw) => (
              <li key={hw.id}>
                <Link to="/curator/homework">
                  <Card interactive className="flex items-center gap-3">
                    <span className="material-symbols-outlined text-on-surface-variant">assignment_late</span>
                    <div className="min-w-0 flex-1">
                      <CardTitle>{hw.student_name || t('curatorDashboard.homework')}</CardTitle>
                      <p className="truncate text-xs text-on-surface-variant">
                        {hw.lesson_title || hw.section_title || t('curatorDashboard.homework')}
                        {' · '}
                        {hw.submitted_at
                          ? t('curatorDashboard.submittedAt', { date: new Date(hw.submitted_at).toLocaleString('ru-RU') })
                          : t('curatorDashboard.submittedAtUnknown')}
                      </p>
                    </div>
                    <Pill variant="warning">{t('curatorDashboard.awaitingReview')}</Pill>
                  </Card>
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <Card variant="subtle" className="text-center text-on-surface-variant">{t('curatorDashboard.allReviewed')}</Card>
        )}
      </section>
    </>
  );
}
